/**
 * PowerPoint host helpers — Office.js Common API + PowerPointApi (PowerPoint.run).
 * Reading, writing, slides, and agentic editing (applyOps).
 * PowerPointApi 1.4+ for reading all slides, 1.5+ for adding slides.
 */

export interface SlideText {
  slide: number;
  texts: string[];
  /** Whether this slide is the one currently being viewed/selected. */
  current?: boolean;
}

export function getSelectedText(): Promise<string> {
  return new Promise((resolve, reject) => {
    Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (res) => {
      if (res.status === Office.AsyncResultStatus.Succeeded) resolve((res.value as string) ?? "");
      else reject(new Error(res.error?.message ?? "无法读取选中文字"));
    });
  });
}

/** Index (1-based) of the slide currently being viewed/edited, or null when
 *  unavailable (older Office versions / no selection). Uses the SlideRange
 *  coercion type, which returns JSON describing the active slide(s). */
export function getCurrentSlideIndex(): Promise<number | null> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.SlideRange, (res) => {
        if (res.status !== Office.AsyncResultStatus.Succeeded) return resolve(null);
        // SlideRange JSON: { slides: [{ id, index, name }], source: "Current"|"All" }
        const raw = res.value;
        let parsed: unknown = raw;
        if (typeof raw === "string") {
          try { parsed = JSON.parse(raw); } catch { /* not JSON */ }
        }
        const slides = (parsed as { slides?: Array<{ index?: unknown }> })?.slides;
        if (Array.isArray(slides) && slides.length && typeof slides[0]?.index === "number") {
          resolve(slides[0].index + 1); // Office index is 0-based → make 1-based
        } else {
          resolve(null);
        }
      });
    } catch {
      resolve(null);
    }
  });
}

export function setSelectedText(text: string): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(text, { coercionType: Office.CoercionType.Text }, (res) => {
      if (res.status === Office.AsyncResultStatus.Succeeded) resolve();
      else reject(new Error(res.error?.message ?? "无法写入选中位置（请先选中一个文本框）"));
    });
  });
}

/** Shape types that don't support a TextFrame; touching .textFrame on them throws InvalidArgument. */
const TEXTLESS_SHAPE_TYPES = new Set(["image", "group", "unsupported", "line"]);

/** Read text from every text-bearing shape on every slide. Requires PowerPointApi 1.4. */
export async function getAllSlidesText(): Promise<SlideText[]> {
  // Resolve the current slide first so each entry can be tagged. Resolved in
  // parallel with the per-slide read below to avoid an extra round-trip.
  const currentIdxPromise = getCurrentSlideIndex();
  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();

    const currentIdx = await currentIdxPromise;
    const result: SlideText[] = [];
    for (let i = 0; i < slides.items.length; i++) {
      const slide = slides.items[i];
      const shapes = slide.shapes;
      shapes.load("items/type");
      await context.sync();

      // Tables, pictures, grouped shapes, lines, charts, etc. throw InvalidArgument
      // when their .textFrame is accessed, so filter those out before touching it.
      const candidates = shapes.items.filter((sh) => !TEXTLESS_SHAPE_TYPES.has(String(sh.type).toLowerCase()));

      const texts: string[] = [];
      for (const sh of candidates) {
        try {
          const tf = sh.textFrame;
          tf.load("hasText");
          await context.sync();
          if (!tf.hasText) continue;

          const r = tf.textRange;
          r.load("text");
          await context.sync();

          const t = (r.text ?? "").trim();
          if (t) texts.push(t);
        } catch {
          // Shape doesn't actually support a TextFrame — skip it without aborting the run.
        }
      }
      const entry: SlideText = { slide: i + 1, texts };
      if (currentIdx !== null && currentIdx === i + 1) entry.current = true;
      result.push(entry);
    }
    return result;
  });
}

/** Append a new slide with a title and bullet body. Requires PowerPointApi 1.5 (add) + 1.4 (textbox). */
export async function addSlide(title: string, bullets: string[]): Promise<number> {
  return PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items");
    await context.sync();
    const beforeCount = slides.items.length;

    slides.add();
    await context.sync();

    const slide = context.presentation.slides.getItemAt(beforeCount);

    const titleBox = slide.shapes.addTextBox(title, { left: 40, top: 28, width: 860, height: 90 });
    titleBox.textFrame.textRange.font.size = 30;
    titleBox.textFrame.textRange.font.bold = true;

    const body = bullets.map((b) => `•  ${b}`).join("\n");
    const bodyBox = slide.shapes.addTextBox(body, { left: 56, top: 140, width: 820, height: 360 });
    bodyBox.textFrame.textRange.font.size = 18;

    await context.sync();
    return beforeCount + 1;
  });
}

export function supportsSlideRead(): boolean {
  try {
    return Office.context.requirements.isSetSupported("PowerPointApi", "1.4");
  } catch {
    return false;
  }
}

export function supportsAddSlide(): boolean {
  try {
    return Office.context.requirements.isSetSupported("PowerPointApi", "1.5");
  } catch {
    return false;
  }
}

import { DocOp } from "../core/types";
import { normalizeBulletList, unescapeLiteralText } from "./sanitize";

/** Execute a list of editing operations against the presentation. */
export async function applyOps(ops: DocOp[]): Promise<string[]> {
  const log: string[] = [];
  for (const op of ops) {
    const kind = String(op.op || "");
    if (kind === "addSlide") {
      // Titles must be single-line; bullets may arrive as one "\n"-joined
      // string — split them into real bullet items.
      const title = unescapeLiteralText(String(op.title ?? "新幻灯片")).replace(/\s*\n\s*/g, " ").trim() || "新幻灯片";
      const bullets = normalizeBulletList(op.bullets);
      const n = await addSlide(title, bullets);
      log.push(`新增第 ${n} 页：${title}`);
    } else if (kind === "addSlides") {
      const slides = Array.isArray(op.slides) ? (op.slides as Array<{ title?: unknown; bullets?: unknown }>) : [];
      for (const s of slides) {
        const title = unescapeLiteralText(String(s.title ?? "新幻灯片")).replace(/\s*\n\s*/g, " ").trim() || "新幻灯片";
        const bullets = normalizeBulletList(s.bullets);
        const n = await addSlide(title, bullets);
        log.push(`新增第 ${n} 页：${title}`);
      }
    } else if (kind === "replaceSelection") {
      await setSelectedText(unescapeLiteralText(String(op.text ?? "")));
      log.push("替换选中文字");
    } else {
      log.push(`忽略未知操作：${kind}`);
    }
  }
  return log;
}
